/**
 * monthlyBilling.ts — Facturation Lil & Co par MOIS CIVIL, PUR & testable.
 *
 * Modèle économique (24/08/2026) :
 *   • Ligne fixe : 128 € HT/mois (maintenance site + automatisation avis Google),
 *     facturée si billing_status = 'active' ;
 *   • Commissions 0,99 €/commande passée via le site :
 *       - payée EN LIGNE  → déjà encaissée via Stripe (rien à facturer),
 *       - payée SUR PLACE → encaissée par le resto → à lui refacturer en fin de mois.
 *
 * Données : RPC `super_commission_stats` (une ligne par mois × resto, montants en €).
 * Le super admin CALCULE tout ; Odoo ne fait que produire la facture envoyée.
 */

export const MAINTENANCE_CENTS = 12800; // 128 € HT / mois (produit Odoo LILCO-MAINT)
export const COMMISSION_CENTS = 99; // 0,99 € / commande site

/** Ligne brute renvoyée par la RPC `super_commission_stats`. */
export interface MonthlyStatRaw {
  month: string; // 'YYYY-MM'
  tenant_slug: string;
  tenant_name: string;
  orders_online: number;
  orders_onsite: number;
  commission_online: number; // €
  commission_onsite: number; // €
  commission_total: number; // €
}

/** Vue d'un resto pour UN mois civil (tout en cents). */
export interface TenantMonth {
  slug: string;
  name: string;
  onlineOrders: number;
  onsiteOrders: number;
  onlineCents: number; // commission déjà encaissée (Stripe)
  onsiteCents: number; // commission à refacturer
  commissionCents: number; // total commissions du mois
  maintenanceCents: number; // 12800 si facturation active, sinon 0
  toInvoiceCents: number; // maintenance + commissions sur place (HT)
}

export interface MonthTotals {
  onlineOrders: number;
  onsiteOrders: number;
  onlineCents: number;
  onsiteCents: number;
  commissionCents: number;
  maintenanceCents: number;
  toInvoiceCents: number;
}

/** Point d'historique : CA commissions d'un mois (réseau entier). */
export interface MonthHistoryPoint {
  month: string;
  onlineCents: number;
  onsiteCents: number;
  commissionCents: number;
}

function num(v: unknown): number {
  const x = typeof v === 'string' ? Number(v) : (v as number);
  return typeof x === 'number' && Number.isFinite(x) ? x : 0;
}

function toCents(euros: unknown): number {
  return Math.round(num(euros) * 100);
}

/** 'YYYY-MM' d'une date. */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/** '2026-07' → 'juillet 2026'. Tolérant : renvoie la clé brute si invalide. */
export function monthLabelFr(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key ?? '');
  if (!m) return key ?? '';
  const idx = Number(m[2]) - 1;
  if (idx < 0 || idx > 11) return key;
  return `${MONTHS_FR[idx]} ${m[1]}`;
}

/** Les `count` derniers mois civils (du plus récent au plus ancien), mois courant inclus. */
export function lastMonths(count = 12, now = new Date()): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(monthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
  }
  return out;
}

/** Sélecteur : derniers mois + tout mois plus ancien présent dans les stats. */
export function selectableMonths(rows: MonthlyStatRaw[], count = 12, now = new Date()): string[] {
  const set = new Set(lastMonths(count, now));
  for (const r of rows ?? []) if (r?.month) set.add(r.month);
  return [...set].sort().reverse();
}

/** Lignes restos d'un mois donné (restos actifs sans commande inclus via `tenants`). */
export function tenantsForMonth(
  rows: MonthlyStatRaw[],
  month: string,
  activeSlugs: Set<string>,
  allTenants: { slug: string; name: string }[],
): TenantMonth[] {
  const bySlug = new Map<string, MonthlyStatRaw>();
  for (const r of rows ?? []) if (r.month === month) bySlug.set(r.tenant_slug, r);

  // Union : restos avec commandes ce mois-là + restos en facturation active (128 € dus même sans commande).
  const slugs = new Set<string>([...bySlug.keys()]);
  for (const t of allTenants) if (activeSlugs.has(t.slug)) slugs.add(t.slug);

  const nameOf = new Map(allTenants.map((t) => [t.slug, t.name]));
  const out: TenantMonth[] = [];
  for (const slug of slugs) {
    const r = bySlug.get(slug);
    const online = Math.max(0, Math.trunc(num(r?.orders_online)));
    const onsite = Math.max(0, Math.trunc(num(r?.orders_onsite)));
    const onlineCents = r ? toCents(r.commission_online) : 0;
    const onsiteCents = r ? toCents(r.commission_onsite) : 0;
    const maintenanceCents = activeSlugs.has(slug) ? MAINTENANCE_CENTS : 0;
    out.push({
      slug,
      name: r?.tenant_name ?? nameOf.get(slug) ?? slug,
      onlineOrders: online,
      onsiteOrders: onsite,
      onlineCents,
      onsiteCents,
      commissionCents: onlineCents + onsiteCents,
      maintenanceCents,
      toInvoiceCents: maintenanceCents + onsiteCents,
    });
  }
  return out.sort((a, b) => b.toInvoiceCents - a.toInvoiceCents || a.name.localeCompare(b.name));
}

/** Totaux réseau du mois sélectionné. */
export function monthTotals(rows: TenantMonth[]): MonthTotals {
  return (rows ?? []).reduce<MonthTotals>(
    (acc, t) => {
      acc.onlineOrders += t.onlineOrders;
      acc.onsiteOrders += t.onsiteOrders;
      acc.onlineCents += t.onlineCents;
      acc.onsiteCents += t.onsiteCents;
      acc.commissionCents += t.commissionCents;
      acc.maintenanceCents += t.maintenanceCents;
      acc.toInvoiceCents += t.toInvoiceCents;
      return acc;
    },
    { onlineOrders: 0, onsiteOrders: 0, onlineCents: 0, onsiteCents: 0, commissionCents: 0, maintenanceCents: 0, toInvoiceCents: 0 },
  );
}

/** Historique réseau : CA commissions par mois (du plus ancien au plus récent). */
export function monthlyHistory(rows: MonthlyStatRaw[]): MonthHistoryPoint[] {
  const byMonth = new Map<string, MonthHistoryPoint>();
  for (const r of rows ?? []) {
    if (!r?.month) continue;
    const p = byMonth.get(r.month) ?? { month: r.month, onlineCents: 0, onsiteCents: 0, commissionCents: 0 };
    p.onlineCents += toCents(r.commission_online);
    p.onsiteCents += toCents(r.commission_onsite);
    p.commissionCents += toCents(r.commission_total);
    byMonth.set(r.month, p);
  }
  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
}

/** Réf de facture Lil & Co d'un resto pour un mois (même convention que les Edge Functions). */
export function invoiceRef(slug: string, month: string): string {
  return `LILCO-${slug}-${month}`;
}
